import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import DataTable from '../components/DataTable';
import { AuthContext } from '../contexts/AuthContext';
import { fetchVerificacionesStock, resolverVerificacionStock } from '../api/verificacionesStock';
import { useLoading } from '../contexts/LoadingContext';

const estados = { pendiente: 'Pendientes', resuelta: 'Resueltas', descartada: 'Descartadas' };

export default function VerificacionesStock() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.rol === 'admin' || user?.rol_id === 1;
  const { stop } = useLoading();
  const [estado, setEstado] = useState('pendiente');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [selected, setSelected] = useState(null);
  const [resolution, setResolution] = useState('');
  const [nextState, setNextState] = useState('resuelta');
  const request = useRef(0);

  const load = useCallback(async () => {
    const current = ++request.current;
    setLoading(true);
    setError('');
    try {
      const result = await fetchVerificacionesStock({ estado, page, pageSize: 20 });
      if (request.current === current) setData(result);
    } catch (err) {
      if (request.current === current) setError(err?.response?.data?.error || 'No se pudieron cargar las verificaciones');
    } finally {
      if (request.current === current) setLoading(false);
    }
  }, [estado, page]);
  useEffect(() => { stop(); }, [stop]);
  useEffect(() => { load(); return () => { request.current += 1; }; }, [load]);

  const resolve = async () => {
    if (!resolution.trim()) { setDialogError('Describe lo que se verificó o la decisión tomada.'); return; }
    setSaving(true);
    setDialogError('');
    try {
      await resolverVerificacionStock(selected.id, { estado: nextState, resolucion: resolution.trim() });
      setSelected(null);
      if (data.rows.length === 1 && page > 1) setPage(page - 1);
      else await load();
    } catch (err) {
      setDialogError(err?.response?.data?.error || 'No se pudo guardar la resolución');
    } finally { setSaving(false); }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h4">Verificación de stock</Typography>
        <Alert severity="info">
          Aquí quedan los productos que impidieron guardar o cobrar una venta por falta de existencias.
          Revisa el inventario físico y los lotes de compra. Resolver un pendiente guarda la decisión;
          después debes volver a guardar o cobrar la venta.
        </Alert>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField select label="Estado" value={estado} sx={{ minWidth: 200 }}
            onChange={(event) => { setPage(1); setData({ rows: [], total: 0 }); setEstado(event.target.value); }}>
            {Object.entries(estados).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField>
          <Button onClick={load} disabled={loading}>Actualizar</Button>
          <Button component={RouterLink} to="/ordenes_compra">Revisar compras</Button>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        <DataTable rows={data.rows} loading={loading} showActions={false}
          pagination={{ page, pageSize: 20, total: data.total, onPageChange: setPage }}
          columns={[
            { Header: 'Producto', accessor: (row) => row.producto || `Producto #${row.producto_id}` },
            { Header: 'SKU', accessor: 'sku' },
            { Header: 'Cliente', accessor: (row) => row.cliente || '—' },
            { Header: 'Venta', accessor: (row) => row.venta_codigo || 'Venta sin guardar' },
            { Header: 'Solicitado', accessor: 'cantidad_solicitada' },
            { Header: 'Faltante al intentar', accessor: 'cantidad_faltante' },
            { Header: 'Stock actual', accessor: 'stock_actual' },
            { Header: 'Disponible en lotes', accessor: 'disponible_lotes' },
            { Header: 'Motivo', accessor: 'motivo' },
            { Header: 'Último intento (UTC)', accessor: 'updated_at' },
            { Header: 'Resolución', accessor: (row) => row.resolucion || 'Pendiente' },
            { Header: 'Acciones', accessor: (row) => (
              <Stack direction="row" spacing={1}>
                <Button size="small" component={RouterLink} to={`/productos/editar/${row.producto_id}`}>Producto</Button>
                {row.venta_id && <Button size="small" component={RouterLink} to={`/ventas/ver/${row.venta_id}`}>Venta</Button>}
                {isAdmin && row.estado === 'pendiente' && <Button size="small" onClick={() => {
                  setSelected(row); setResolution(''); setNextState('resuelta'); setDialogError('');
                }}>Atender</Button>}
              </Stack>
            ) },
          ]}
        />
      </Stack>
      <Dialog open={Boolean(selected)} onClose={() => !saving && setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>Verificar {selected?.producto || 'producto'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">Registra el resultado de la revisión. El stock se corrige desde productos o compras, según corresponda.</Alert>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
            <TextField select label="Resultado" value={nextState} onChange={(event) => setNextState(event.target.value)}>
              <MenuItem value="resuelta">Resuelta</MenuItem>
              <MenuItem value="descartada">Descartada</MenuItem>
            </TextField>
            <TextField label="Resolución" required multiline minRows={3} value={resolution}
              inputProps={{ maxLength: 500 }} onChange={(event) => setResolution(event.target.value)}
              helperText="Ejemplo: se recibió la compra pendiente, se corrigió el producto o se descartó la solicitud." />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setSelected(null)}>Cancelar</Button>
          <Button disabled={saving} variant="contained" onClick={resolve}>Guardar resolución</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
